// src/components/TripComments.js
import React, { useState, useEffect } from 'react';
import { addTripComment, getTripComments, deleteTripComment, updateTripComment } from '../services/tripRatingService';
import { showConfirm, showDeleteConfirm, showSuccess, showError } from '../services/alertService';
import './TripComments.css';

const TripComments = ({ trip, user }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    if (trip.id) {
      fetchComments();
    }
  }, [trip.id]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const commentsData = await getTripComments(trip.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      await showError(
        '請輸入留言內容',
        '留言內容不能為空，請輸入你想分享的內容。'
      );
      return;
    }

    try {
      setSubmitting(true);
      await addTripComment(
        trip.id,
        user.uid,
        user.displayName,
        user.photoURL,
        newComment
      );
      setNewComment('');
      await fetchComments();
      await showSuccess(
        '留言發送成功！',
        '你的留言已成功發布，其他用戶現在可以看到了。'
      );
    } catch (error) {
      console.error('Error submitting comment:', error);
      await showError(
        '留言發送失敗',
        '發送留言時發生錯誤，請檢查網路連線後再試。'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const result = await showDeleteConfirm(
      '刪除留言',
      '確定要刪除這則留言嗎？刪除後將無法復原。'
    );

    if (result) {
      try {
        await deleteTripComment(commentId, user.uid);
        await fetchComments();
        await showSuccess(
          '留言已刪除',
          '你的留言已成功刪除。'
        );
      } catch (error) {
        console.error('Error deleting comment:', error);
        if (error.message.includes('只能刪除自己的留言')) {
          await showError(
            '刪除失敗',
            '你只能刪除自己的留言。'
          );
        } else {
          await showError(
            '刪除失敗',
            '刪除留言時發生錯誤，請稍後再試。'
          );
        }
      }
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditText(comment.comment);
  };

  const handleUpdateComment = async (commentId) => {
    if (!editText.trim()) {
      await showError(
        '留言內容不能為空',
        '請輸入留言內容。'
      );
      return;
    }

    try {
      await updateTripComment(commentId, user.uid, editText);
      setEditingComment(null);
      setEditText('');
      await fetchComments();
      await showSuccess(
        '留言更新成功！',
        '你的留言已成功更新。'
      );
    } catch (error) {
      console.error('Error updating comment:', error);
      if (error.message.includes('只能修改自己的留言')) {
        await showError(
          '更新失敗',
          '你只能修改自己的留言。'
        );
      } else {
        await showError(
          '更新失敗',
          '更新留言時發生錯誤，請稍後再試。'
        );
      }
    }
  };

  const handleCancelEdit = async () => {
    if (editText !== comments.find(c => c.id === editingComment)?.comment) {
      const result = await showConfirm(
        '取消編輯',
        '你的修改尚未保存，確定要取消編輯嗎？',
        {
          confirmButtonText: '確定取消',
          cancelButtonText: '繼續編輯',
          confirmButtonColor: '#6b7280'
        }
      );
      
      if (result) {
        setEditingComment(null);
        setEditText('');
      }
    } else {
      setEditingComment(null);
      setEditText('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString.seconds ? new Date(dateString.seconds * 1000) : new Date(dateString);
    
    const now = new Date();
    const diffTime = now - date;
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return '剛剛';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} 分鐘前`;
    } else if (diffHours < 24) {
      return `${diffHours} 小時前`;
    } else if (diffDays < 7) {
      return `${diffDays} 天前`;
    } else {
      return date.toLocaleDateString('zh-TW');
    }
  };

  return (
    <div className="trip-comments">
      <div className="comments-header">
        <h3>💬 行程留言 ({comments.length})</h3>
        <p>分享你的想法或詢問問題</p>
      </div>

      {/* 留言輸入區域 */}
      <div className="comment-input-section">
        <div className="user-avatar">
          <img src={user.photoURL} alt={user.displayName} />
        </div>
        <form onSubmit={handleSubmitComment} className="comment-form">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="寫下你的留言..."
            rows="3"
            maxLength="500"
            className="comment-textarea"
          />
          <div className="comment-form-footer">
            <span className="char-count">
              {newComment.length}/500
            </span>
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="submit-comment-btn"
            >
              {submitting ? (
                <>
                  <span className="alert-loading"></span>
                  發送中...
                </>
              ) : (
                <>💌 發送留言</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 留言列表 */}
      <div className="comments-list">
        {loading ? (
          <div className="comments-loading">
            載入留言中...
          </div>
        ) : comments.length === 0 ? (
          <div className="no-comments">
            <div className="no-comments-icon">💭</div>
            <p>還沒有人留言</p>
            <p>成為第一個分享想法的人！</p>
          </div>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="comment-item">
              <div className="comment-avatar">
                <img 
                  src={comment.userPhoto || '/default-avatar.png'} 
                  alt={comment.userName}
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.userName)}&background=3b82f6&color=fff`;
                  }}
                />
              </div>
              
              <div className="comment-content">
                <div className="comment-header">
                  <span className="comment-author">{comment.userName}</span>
                  <span className="comment-time">{formatDate(comment.createdAt)}</span>
                  
                  {comment.userId === user.uid && (
                    <div className="comment-actions">
                      <button
                        onClick={() => handleEditComment(comment)}
                        className="edit-comment-btn"
                        title="編輯留言"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="delete-comment-btn"
                        title="刪除留言"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
                
                {editingComment === comment.id ? (
                  <div className="edit-comment-form">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows="3"
                      maxLength="500"
                      className="edit-textarea"
                    />
                    <div className="edit-actions">
                      <button
                        onClick={handleCancelEdit}
                        className="cancel-edit-btn"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => handleUpdateComment(comment.id)}
                        className="save-edit-btn"
                      >
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-text">
                    {comment.comment}
                    {comment.updatedAt && 
                     new Date(comment.updatedAt.seconds * 1000).getTime() !== 
                     new Date(comment.createdAt.seconds * 1000).getTime() && (
                      <span className="edited-indicator"> (已編輯)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TripComments;